import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Optional,
  Record as RRecord,
  Static,
  String,
  Union,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

import { EffortLevel } from "./effortLevel.js";
import { ModelFeatures } from "./ModelFeatures.js";

/**
 * Effort tiers accepted by OpenAI-compatible reasoning APIs. Support is
 * model-specific; most OpenAI models top out at `xhigh`.
 */
export const OpenAiReasoningEffortLiteral = Union(
  Literal("none"),
  Literal("minimal"),
  Literal("low"),
  Literal("medium"),
  Literal("high"),
  Literal("xhigh"),
  Literal("max"),
);
export type OpenAiReasoningEffort = Static<typeof OpenAiReasoningEffortLiteral>;

export const OpenAiFastServiceTierLiteral = Union(
  Literal("fast"),
  Literal("priority"),
);
export type OpenAiFastServiceTier = Static<typeof OpenAiFastServiceTierLiteral>;

/** Canonical {@link EffortLevel} → OpenAI API param; `max` collapses to `xhigh`. */
export function toOpenAi(p: EffortLevel): OpenAiReasoningEffort {
  return p === "max" ? "xhigh" : p;
}

/** @deprecated */
export const DeprecatedOpenAiChatModelLiteral = Union(
  Literal("GPT_4_OMNI"),
  Literal("GPT_4_OMNI_MINI"),
  Literal("GPT_5"),
  Literal("GPT_51"),
  Literal("GPT_53_CODEX"),
  Literal("O3"),
  Literal("GPT_41_MINI"),
  Literal("GPT_54_NANO"),
  Literal("GPT_41_NANO"),
  Literal("GPT_52"),
  Literal("CREST_ALPHA"),
  Literal("JOULE_ALPHA"),
  Literal("FIREFLY_ALPHA"),
);
/** @deprecated */
export type DeprecatedOpenAiChatModel = Static<
  typeof DeprecatedOpenAiChatModelLiteral
>;
/** @deprecated */
export const DeprecatedOpenAiChatModel = getNormalEnum(
  DeprecatedOpenAiChatModelLiteral,
);

export const OpenAiChatModelLiteral = Union(
  Literal("GPT_41"),
  Literal("GPT_54"),
  Literal("GPT_54_MINI"),
  Literal("GPT_55"),
  Literal("GPT_56"),
  Literal("GPT_56_TERRA"),
  Literal("GPT_56_LUNA"),
  Literal("ULTIMA_ALPHA"),
  Literal("GPT_NEXT_V2_AUG_2026"),
);
export type OpenAiChatModel = Static<typeof OpenAiChatModelLiteral>;
export const OpenAiChatModel = getNormalEnum(OpenAiChatModelLiteral);

type OpenAiChatModelFeatures = ModelFeatures & {
  // OpenAI documentation of how they count tokens seems to be kinda off, so
  // for now we'll just subtract 10 from the max tokens to be safe.
  maxInputTokens: number;
  maxOutputTokens: number;
  tokensPerMessage: number;
  /** Model accepts the `reasoning_effort` API param at all. */
  effort?: true;
  /** Accepts `none` reasoning. */
  noneEffort?: true;
  /** Accepts `minimal` reasoning (OpenAI added this in 5.1). */
  minimalEffort?: true;
  /** Accepts `xhigh` reasoning. */
  xHighEffort?: true;
  /** Accepts `max` reasoning. */
  maxEffort?: true;
};

export const OpenAiChatModelFeatureMap: Record<
  OpenAiChatModel,
  OpenAiChatModelFeatures
> = {
  [OpenAiChatModel.GPT_41]: {
    name: "gpt-4.1-2025-04-14",
    label: "GPT 4.1",
    requiresDataRetention: false,
    maxInputTokens: 400000 - 16384 - 10,
    maxOutputTokens: 16384,
    tokensPerMessage: 3,
  },
  [OpenAiChatModel.GPT_54]: {
    name: "gpt-5.4",
    label: "GPT 5.4",
    requiresDataRetention: false,
    maxInputTokens: 1000000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    effort: true,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_54_MINI]: {
    name: "gpt-5.4-mini",
    label: "GPT 5.4 Mini",
    requiresDataRetention: false,
    maxInputTokens: 400000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    effort: true,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_55]: {
    name: "gpt-5.5",
    label: "GPT 5.5",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    effort: true,
    noneEffort: true,
    minimalEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_56]: {
    name: "gpt-5.6",
    label: "GPT 5.6 Sol",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    effort: true,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_56_TERRA]: {
    name: "gpt-5.6-terra",
    label: "GPT 5.6 Terra",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    effort: true,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.GPT_56_LUNA]: {
    name: "gpt-5.6-luna",
    label: "GPT 5.6 Luna",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    effort: true,
    noneEffort: true,
    xHighEffort: true,
  },
  [OpenAiChatModel.ULTIMA_ALPHA]: {
    name: "ultima-alpha",
    label: "Ultima Alpha",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    effort: true,
    noneEffort: true,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_NEXT_V2_AUG_2026]: {
    name: "vega-alpha",
    label: "gpt-next-v2-aug-2026",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    effort: true,
    noneEffort: true,
    xHighEffort: true,
    maxEffort: true,
  },
};

/**
 * Assistant phase metadata from the OpenAI Responses API.
 */
export const OpenAIAssistantPhaseLiteral = Union(
  Literal("commentary"),
  Literal("final_answer"),
);
export type OpenAIAssistantPhase = Static<typeof OpenAIAssistantPhaseLiteral>;
export const isOpenAIAssistantPhase = (
  phase: unknown,
): phase is OpenAIAssistantPhase =>
  phase === "commentary" || phase === "final_answer";

// These params are not allowed to be set for reasoning models
// The API will return a 400 if they are
export const openAiReasoningModelChatCompletionParams = {
  temperature: undefined,
  parallel_tool_calls: undefined,
};
export const CustomOpenAiModelConfig = RRecord({
  model: String,
  maxInputTokens: Number,
  maxOutputTokens: Optional(Number), // Not used by reasoning models
  /**
   * Set to `true` for multimodal deployments that accept `input_image` content
   * parts on user messages. When `false` or omitted, image tool results are
   * elided to a short text placeholder rather than dumped as base64 text.
   *
   * OpenAI vendor models (the `OpenAiChatModelLiteral` union) always support
   * vision; this flag only governs custom/gateway deployments.
   */
  supportsVision: Optional(Boolean),
});
export type CustomOpenAiModelConfig = Static<typeof CustomOpenAiModelConfig>;

export const OpenAiModelParams = RRecord({
  frequencyPenalty: Number,
  n: Number,
  presencePenalty: Number,
  stopWords: Array(String),
  temperature: Optional(Number), // Not used by reasoning models
  topP: Number,
  reasoningEffort: Optional(OpenAiReasoningEffortLiteral), // ONLY used by reasoning models
  serviceTier: Optional(OpenAiFastServiceTierLiteral),
  verbosity: Optional(
    Union(Literal("low"), Literal("medium"), Literal("high")),
  ),
  model: Union(OpenAiChatModelLiteral, CustomOpenAiModelConfig),
  toolSearch: Optional(
    RRecord({
      enabled: Boolean,
      /** Tool names that should never be deferred (always loaded into context) */
      alwaysLoadedTools: Optional(Array(String)),
    }),
  ),
});
export type OpenAiModelParams = Static<typeof OpenAiModelParams>;

export const isOpenAiFastModeParams = (
  modelParams: OpenAiModelParams,
): boolean =>
  modelParams.model === OpenAiChatModel.GPT_56 &&
  OpenAiFastServiceTierLiteral.guard(modelParams.serviceTier);

/**
 * Maps the requested OpenAI effort tier to the value to send, clamping tiers
 * the target model doesn't support down to the next-best supported one. Custom
 * (non-vendor) models are passed through unchanged.
 */
export function buildOpenAiEffortForRequest(
  modelParams: OpenAiModelParams,
): OpenAiReasoningEffort | null {
  const effort = modelParams.reasoningEffort ?? null;
  if (effort == null) return null;
  if (!OpenAiChatModelLiteral.guard(modelParams.model)) return effort;
  const features = OpenAiChatModelFeatureMap[modelParams.model];
  if (!features.effort) return null;
  if (effort === "none" && !features.noneEffort) return "low";
  if (effort === "minimal" && !features.minimalEffort) return "low";
  if (effort === "xhigh" && !features.xHighEffort) return "high";
  if (effort === "max" && !features.maxEffort) {
    return features.xHighEffort ? "xhigh" : "high";
  }
  return effort;
}

export const OpenAiToolSearchConfig = RRecord({
  enabled: Boolean,
  /** Tool names that should never be deferred (always loaded into context) */
  alwaysLoadedTools: Optional(Array(String)),
});
export type OpenAiToolSearchConfig = Static<typeof OpenAiToolSearchConfig>;

export const OpenAiCompactionConfig = RRecord({
  enabled: Boolean,
  compactThreshold: Optional(Number),
});
export type OpenAiCompactionConfig = Static<typeof OpenAiCompactionConfig>;

export const OpenAiDefaultCompactionConfig: OpenAiCompactionConfig = {
  enabled: true,
  compactThreshold: 300000,
};

// Based on `ChatCompletionRequestMessageRoleEnum` from openai package
export const OpenAiChatMessageRoleLiteral = Union(
  Literal("system"),
  Literal("user"),
  Literal("assistant"),
  Literal("tool"),
);
export type OpenAiChatMessageRole = Static<typeof OpenAiChatMessageRoleLiteral>;
export const OpenAiChatMessageRole = getNormalEnum(
  OpenAiChatMessageRoleLiteral,
);

// based on `ChatCompletionContentPartImage` from openai package
const OpenAiChatContentPartImage = RRecord({
  type: Literal("image_url"),
  image_url: RRecord({
    url: String,
  }),
});

// based on `ChatCompletionContentPartFile` from openai package
// See https://developers.openai.com/api/docs/guides/pdf-files/
const OpenAiChatContentPartFile = RRecord({
  type: Literal("file"),
  file: RRecord({
    filename: String,
    file_data: String, // base64-encoded file with data URI prefix
  }),
});

const OpenAiChatContentPart = Union(
  OpenAiChatContentPartImage,
  OpenAiChatContentPartFile,
);

// Based on `ChatCompletionRequestMessage` from openai package
export const OpenAiChatMessage = Union(
  RRecord({
    content: String,
    role: Literal(OpenAiChatMessageRole.system),
  }),
  RRecord({
    content: String.Or(Array(OpenAiChatContentPart)),
    role: Literal(OpenAiChatMessageRole.user),
  }),
  RRecord({
    content: String.Or(Null),
    // OpenAI requires snake case
    function_call: Optional(RRecord({ arguments: String, name: String })),
    reasoning_content: Optional(String),
    role: Literal(OpenAiChatMessageRole.assistant),
  }),
  RRecord({
    content: String,
    role: Literal(OpenAiChatMessageRole.tool),
    tool_call_id: String,
  }),
  RRecord({
    content: Optional(String.Or(Null)),
    reasoning_content: Optional(String),
    tool_calls: Array(
      RRecord({
        type: Literal("function"),
        id: String,
        function: RRecord({ name: String, arguments: String }),
      }),
    ),
    role: Literal(OpenAiChatMessageRole.assistant),
  }),
);
export type OpenAiChatMessage = Static<typeof OpenAiChatMessage>;

export const OpenAIConformerResult = RRecord({
  messages: Array(OpenAiChatMessage),
});
export type OpenAIConformerResult = Static<typeof OpenAIConformerResult>;

/**
 * Determines if a model name corresponds to a reasoning model
 * Reasoning models use max_completion_tokens instead of max_tokens
 */
export function isReasoningModel(modelName: string): boolean {
  if (
    modelName.startsWith(
      OpenAiChatModelFeatureMap[OpenAiChatModel.GPT_NEXT_V2_AUG_2026].name,
    )
  ) {
    return true;
  }

  // Check if it's a reasoning model based on naming patterns
  const reasoningModelPatterns = [
    /^o1/i, // o1-preview, o1-mini, etc.
    /^o3/i, // o3-mini, o3, etc.
    /^gpt-5/i, // gpt-5 models
    /^ultima-alpha/i, // ultima alpha reasoning model
    /gpt-4\.1/i, // gpt-4.1 models
    /^robin-alpha/i, // robin alpha reasoning model, temp
  ];

  return reasoningModelPatterns.some((pattern) => pattern.test(modelName));
}
